/**
 * 登录页：手机号 + 密码。
 *
 * 首版不接短信：初始密码为手机号后 6 位；首次登录只返回 mustChangePassword，
 * 前端切到改密表单，改密成功后才拿到正式 token。
 */
import { useEffect, useState } from 'react';
import type { Member } from '@hew3/shared';
import { ApiError, api } from '../../lib/api.ts';
import { trackApiError, trackFunnel, trackPageView } from '../../lib/analytics.ts';
import { useAuth } from '../../store/auth.ts';
import { Button, Card } from '../../components/ui.tsx';
import { DEMO_ACCOUNT_LABEL, DEMO_ACCOUNT_NOTE, DEMO_ACCOUNT_PHONE } from './demoAccount.dev.ts';

interface PasswordLoginResult {
  mustChangePassword: boolean;
  token?: string;
  member?: Member;
}

interface PasswordChangeResult {
  token: string;
  member: Member;
}

const DEV = import.meta.env.DEV;

export function LoginPage() {
  const setSession = useAuth((s) => s.setSession);
  const [stage, setStage] = useState<'login' | 'change'>('login');
  const [phone, setPhone] = useState(DEV ? DEMO_ACCOUNT_PHONE : '');
  const [password, setPassword] = useState(DEV ? DEMO_ACCOUNT_PHONE.slice(-6) : '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    trackPageView('/login');
    trackFunnel('login', 'view');
  }, []);

  async function submitLogin(): Promise<void> {
    if (phone.length !== 11 || password.length === 0 || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.post<PasswordLoginResult>('/api/auth/password/login', { phone, password });
      if (result.mustChangePassword) {
        setStage('change');
        trackFunnel('login', 'password_change_required');
        return;
      }
      if (!result.token) throw new Error('登录响应缺少 token');
      trackFunnel('login', 'logged_in');
      await setSession(result.token);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : '登录失败，请稍后重试';
      setError(message);
      if (err instanceof ApiError) trackApiError('/login', err.status, '/api/auth/password/login');
    } finally {
      setBusy(false);
    }
  }

  async function submitChange(): Promise<void> {
    if (busy) return;
    if (newPassword.length < 8) {
      setError('新密码至少 8 位');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('两次输入的新密码不一致');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await api.post<PasswordChangeResult>('/api/auth/password/change', {
        phone,
        oldPassword: password,
        newPassword,
      });
      trackFunnel('login', 'password_changed');
      await setSession(result.token);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : '修改密码失败，请稍后重试';
      setError(message);
      if (err instanceof ApiError) trackApiError('/login', err.status, '/api/auth/password/change');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-root">
      <div className="stage">
        <div className="device-wrap">
          <div className="device">
            <div className="device-shadow" />
            <div className="screen">
              <div className="notch" />
              <main className="viewport">
                <div className="login-hero">
                  <div className="lh-brand">HEW³</div>
                  <div className="lh-sub">
                    三螺旋企业家生态健康守护系统
                    <br />
                    健康 · 企业 · 财富 协同演化引擎
                  </div>
                </div>

                <div className="page">
                  <Card>
                    <div className="field">
                      <label className="field-label" htmlFor="phone">
                        手机号
                      </label>
                      <input
                        id="phone"
                        className="field-input"
                        inputMode="numeric"
                        autoComplete="tel"
                        placeholder="请输入 11 位手机号"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      />
                    </div>

                    <div className="field">
                      <label className="field-label" htmlFor="password">
                        {stage === 'login' ? '密码' : '初始密码'}
                      </label>
                      <input
                        id="password"
                        className="field-input"
                        type="password"
                        autoComplete="current-password"
                        placeholder={stage === 'login' ? '请输入密码' : '手机号后 6 位'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>

                    {stage === 'change' && (
                      <>
                        <div className="field-hint">
                          首次登录需修改初始密码。初始密码为手机号后 6 位，新密码至少 8 位且包含字母和数字。
                        </div>
                        <div className="field">
                          <label className="field-label" htmlFor="newPassword">
                            新密码
                          </label>
                          <input
                            id="newPassword"
                            className="field-input"
                            type="password"
                            autoComplete="new-password"
                            placeholder="至少 8 位，含字母和数字"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                          />
                        </div>
                        <div className="field">
                          <label className="field-label" htmlFor="confirmPassword">
                            确认新密码
                          </label>
                          <input
                            id="confirmPassword"
                            className="field-input"
                            type="password"
                            autoComplete="new-password"
                            placeholder="再次输入新密码"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                        </div>
                      </>
                    )}

                    {error && <div className="field-error">{error}</div>}

                    <div className="sp12" />
                    {stage === 'login' ? (
                      <Button
                        block
                        variant="primary"
                        disabled={busy || phone.length !== 11 || password.length === 0}
                        onClick={() => void submitLogin()}
                      >
                        {busy ? '正在登录…' : '登录'}
                      </Button>
                    ) : (
                      <>
                        <Button
                          block
                          variant="primary"
                          disabled={busy || newPassword.length < 8 || confirmPassword.length === 0}
                          onClick={() => void submitChange()}
                        >
                          {busy ? '正在保存…' : '保存新密码并登录'}
                        </Button>
                        <div className="sp12" />
                        <Button block disabled={busy} onClick={() => { setStage('login'); setError(''); }}>
                          返回登录
                        </Button>
                      </>
                    )}
                  </Card>

                  {DEV && (
                    <div className="note-src" style={{ marginTop: 12 }}>
                      <b>{DEMO_ACCOUNT_LABEL}</b>
                      <br />
                      {DEMO_ACCOUNT_NOTE}
                    </div>
                  )}

                  <div className="sec-note">
                    平台遵循「三绝不」原则：绝不公开成员任何私人信息、绝不未经授权调用数据、绝不变更数据用途。
                    登录后可在「我的 · 隐私与授权」中逐项开启或撤回数据授权。
                  </div>
                </div>
              </main>
            </div>
          </div>
          <div className="device-caption">390 × 844 · 手机端为主形态</div>
        </div>
      </div>
    </div>
  );
}
